import { ReactElement, ReactNode, useCallback, useEffect } from 'react';
import { useIsMounted } from '../../../util/hooks';
import { useWindowSizeWithRotation } from '../../../util/window';
import { BottomPanel, BottomPanelProps } from './bottom-panel';
import { SidePanel } from './side-panel';

interface ChildProps {
  onClose: (cb: () => void) => void;
  dismiss: () => Promise<void>;
}

export interface PanelBaseProps {
  children: ReactNode | ((props: ChildProps) => ReactNode);
  show?: boolean;
  onPanelChange?: (open: boolean, panel: 'side' | 'bottom') => void;
  onDidDismiss?: () => Promise<void> | void;
  disableClose?: boolean;
  canScrollBackdrop?: boolean;
  bottomPanelProps?: BottomPanelProps;
}

const ContentPanel = (props: PanelBaseProps): ReactElement => {
  const {
    children,
    onDidDismiss,
    bottomPanelProps,
    show,
    disableClose,
    onPanelChange,
  } = props;

  const { width } = useWindowSizeWithRotation();

  const shouldUseBottomPanel = isBottomPanel(width);

  const shouldShow = show ?? children !== undefined;

  const showBottom = shouldShow && shouldUseBottomPanel;
  const showSide = shouldShow && !shouldUseBottomPanel;

  const isMounted = useIsMounted();

  let component: ReactElement = <></>;

  const restrictWidth = (classes: string[]) => {
    classes.forEach((className) => {
      const els = document.getElementsByClassName(className);
      for (let i = 0; i < els.length; i++) {
        els[i].classList.add('contrained-side-panel-width');
      }
    });
  };

  const resetMaxWidth = (classes: string[]) => {
    classes.forEach((className) => {
      const els = document.getElementsByClassName(className);
      for (let i = 0; i < els.length; i++) {
        els[i].classList.remove('contrained-side-panel-width');
      }
    });
  };

  const addSidePanelProps = useCallback(() => {
    restrictWidth(['placeholder-item', 'flow-item', 'flow-item-section']);
  }, []);

  const removeSidePanelProps = useCallback(() => {
    resetMaxWidth(['flow-item', 'flow-item-section']);
  }, []);

  useEffect(() => {
    if (typeof onPanelChange === 'function') {
      const panel = shouldUseBottomPanel ? 'bottom' : 'side';
      onPanelChange(shouldShow, panel);
    }
  }, [onPanelChange, shouldShow, shouldUseBottomPanel, showBottom, showSide]);

  useEffect(() => {
    if (showSide) {
      addSidePanelProps();
    } else {
      removeSidePanelProps();
    }
  }, [addSidePanelProps, removeSidePanelProps, showSide]);

  if (children && isMounted()) {
    component = (
      // removed wrapping div and letting side-panel defined slot='fixed' for parent ion-content
      <>
        <SidePanel
          show={showSide}
          disableClose={disableClose}
          onDidDismiss={() => {
            if (onDidDismiss && !shouldUseBottomPanel) {
              onDidDismiss();
            }
          }}
        >
          {!shouldUseBottomPanel && children}
        </SidePanel>
        <BottomPanel
          show={showBottom}
          canScrollBackdrop={true}
          disableClose={disableClose}
          onDidDismiss={() => {
            if (onDidDismiss && shouldUseBottomPanel) {
              onDidDismiss();
            }
          }}
          {...bottomPanelProps}
        >
          {shouldUseBottomPanel && children}
        </BottomPanel>
      </>
    );
  }

  return component;
};

const isBottomPanel = (width: number, widthThreshold = 1000): boolean => {
  return width < (widthThreshold ?? 1000);
};

export { ContentPanel };
